import { api } from '../core/client';
import { wirePhone } from '../core/wire';

/** A reservation as returned by the ThaliOS Web API. */
export interface ApiReservation {
  id: string;
  name: string;
  phone: string;
  party: number;
  when: string;
  date: string;
  from: string;
  to: string;
  tables: string[];
  note?: string;
  status: 'confirmed' | 'seated' | 'pending' | 'cancelled' | 'noshow';
}

/** Body for a customer booking request (POST /api/reservation). */
export interface CreateReservationPayload {
  name: string;
  phone: string;
  party: number;
  when: string;
  /** Requested date, "dd-MM-yyyy" — the server rejects anything before today. */
  date: string;
  /** Requested time, 24h "HH:mm" — must be inside the outlet's open/close hours. */
  time: string;
  /** Table the customer picked, e.g. "T5". Must exist and be enabled in the panel. */
  tables?: string;
  note?: string;
}

/** A table a customer may book — only tables enabled in the panel. */
export interface ApiBookableTable {
  id: string;
  label: string;
  seats: number;
  floor: string;
}

/** Everything the booking screen needs, so the client never hardcodes slots or tables. */
export interface ApiBookingInfo {
  /** Outlet hours, 24h "HH:mm". */
  openTime: string;
  closeTime: string;
  /** Bookable times inside the open/close window, as display text ("7:30 PM"). */
  slots: string[];
  /** Enabled tables only — the count matches the panel exactly. */
  tables: ApiBookableTable[];
  /** Today in "dd-MM-yyyy" — the earliest date a customer may pick. */
  minDate: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string | null;
  result: T;
}

/** Reservations API — GET /api/reservation, POST /api/reservation, GET /api/reservation/booking-info. */
export const reservationService = {
  list(): Promise<ApiReservation[]> {
    // Always resolve to an array — a null/absent result (e.g. an older backend) must not crash callers.
    return api.get<ApiEnvelope<ApiReservation[]>>('/reservation').then((r) => (Array.isArray(r.data.result) ? r.data.result : []));
  },
  /** A single customer's booking requests (by phone), newest first. */
  mine(phone: string): Promise<ApiReservation[]> {
    return api
      .get<ApiEnvelope<ApiReservation[]>>('/reservation', { params: { phone: wirePhone(phone) } })
      .then((r) => (Array.isArray(r.data.result) ? r.data.result : []));
  },
  /** Open/close hours, their slots, the enabled tables, and the earliest date. */
  bookingInfo(): Promise<ApiBookingInfo> {
    return api.get<ApiEnvelope<ApiBookingInfo>>('/reservation/booking-info').then((r) => r.data.result);
  },
  /** Submit a table booking request → returns the created (pending) reservation. */
  create(payload: CreateReservationPayload): Promise<ApiReservation> {
    return api.post<ApiEnvelope<ApiReservation>>('/reservation', payload).then((r) => r.data.result);
  },
  /** Move a booking through its lifecycle — persists (was panel-local state only). */
  setStatus(resId: string, status: ApiReservation['status']): Promise<void> {
    return api.post(`/reservation/${encodeURIComponent(resId)}/status`, { status }).then(() => undefined);
  },
  /** Assign the tables a booking holds ("T1,T2"; blank clears). Tables must be enabled. */
  setTables(resId: string, tables: string[]): Promise<void> {
    return api
      .post(`/reservation/${encodeURIComponent(resId)}/tables`, { tables: tables.join(',') })
      .then(() => undefined);
  }
};
